import {getTestProps} from '@benchling/js-test-utils';
import {Modal, Button, Group} from '@mantine/core';
import React from 'react';

import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import ModalFooter from 'ui-library/modals/ModalFooter';

/** Describes how the transition(s) were triggered, to tailor the modal body text. */
export enum PromoteTransitionContext {
  CONNECT = 'connect',
  SELECT_TASK_SCHEMA = 'select-task-schema',
}

export interface PromoteTransitionModalProps {
  onRequestSubmit: (promote: boolean) => void;
  onRequestClose: () => void;
  opened?: boolean;
  transitionNames: Array<{sourceNodeName: string; targetNodeName: string}>;
  context?: PromoteTransitionContext;
}

export default function PromoteTransitionModal(props: PromoteTransitionModalProps): JSX.Element {
  const {
    onRequestSubmit,
    onRequestClose,
    opened = true,
    transitionNames,
    context = PromoteTransitionContext.CONNECT,
  } = props;

  function formatTransitionName(sourceNodeName: string, targetNodeName: string): string {
    return `${sourceNodeName} → ${targetNodeName}`;
  }

  const actionString =
    context === PromoteTransitionContext.SELECT_TASK_SCHEMA
      ? 'Selecting this task schema'
      : 'Connecting these tasks';

  return (
    <Modal
      opened={opened}
      onClose={onRequestClose}
      title="Add to planned transitions?"
      size="md"
      zIndex={ZINDEX_FLOATING_UI}
      closeButtonProps={{'aria-label': 'Close'}}
    >
      {transitionNames.length === 1 ? (
        <p>
          {actionString} would create a planned transition (
          {formatTransitionName(transitionNames[0].sourceNodeName, transitionNames[0].targetNodeName)}) that
          is currently configured as optional. A transition can't be both optional and planned.
        </p>
      ) : (
        <>
          <p>
            {actionString} would create the {transitionNames.length} planned transitions that are currently
            configured as optional:
          </p>
          <ul>
            {transitionNames.map(({sourceNodeName, targetNodeName}) => (
              <li key={`${sourceNodeName}-${targetNodeName}`}>
                {formatTransitionName(sourceNodeName, targetNodeName)}
              </li>
            ))}
          </ul>
          <p>A transition can't be both optional and planned.</p>
        </>
      )}
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        supportingInfo="Planned transitions are included by default when a flowchart is created from this schema. Optional transitions aren't part of the standard flow, but the person executing this workflow can choose to use them if needed."
      />
      <ModalFooter>
        <Group position="right">
          <Button
            variant="tertiary"
            onClick={onRequestClose}
            {...getTestProps({component: 'PromoteTransitionModal', element: 'keepAsOptionalButton'})}
          >
            Keep as optional
          </Button>
          <Button
            onClick={() => {
              onRequestSubmit(true);
            }}
            data-autofocus={true}
            {...getTestProps({component: 'PromoteTransitionModal', element: 'addToPlannedButton'})}
          >
            Add to planned transitions
          </Button>
        </Group>
      </ModalFooter>
    </Modal>
  );
}
